import { useState, useEffect } from 'react';
import { FaSyncAlt, FaTimes } from 'react-icons/fa';
import { toast } from 'react-hot-toast';

export default function ServiceWorkerUpdatePrompt() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    // Listen for service worker update events
    const handleSWUpdate = (event: CustomEvent) => {
      setUpdateAvailable(true);
      if (event.detail?.registration) {
        setRegistration(event.detail.registration);
      }
    };

    window.addEventListener('sw-update', handleSWUpdate as EventListener);

    // Also check if there's an existing update
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg) {
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  setUpdateAvailable(true);
                  setRegistration(reg);
                }
              });
            }
          });
        }
      });
    }

    return () => {
      window.removeEventListener('sw-update', handleSWUpdate as EventListener);
    };
  }, []);

  const handleUpdate = async () => {
    if (registration?.waiting) {
      // Tell the service worker to skip waiting and activate
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Reload the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } else if (registration) {
      // Force update check
      await registration.update();
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    }
  };

  const handleDismiss = () => {
    setUpdateAvailable(false);
    // Store dismissal in sessionStorage
    sessionStorage.setItem('sw-update-dismissed', Date.now().toString());
  };

  // Check if user dismissed this update
  useEffect(() => {
    const dismissed = sessionStorage.getItem('sw-update-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Re-show after 5 minutes
      if (Date.now() - dismissedTime > 5 * 60 * 1000) {
        sessionStorage.removeItem('sw-update-dismissed');
      } else {
        setUpdateAvailable(false);
      }
    }
  }, []);

  if (!updateAvailable || !registration) {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-blue-600 text-white rounded-lg shadow-lg z-50 p-4 safe-top">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="flex-shrink-0 mt-0.5">
            <FaSyncAlt className="w-5 h-5 animate-spin" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold mb-1">
              Update Available
            </h3>
            <p className="text-xs text-blue-100">
              A new version of Feed Cowdemy is available. Update now for the latest features and improvements.
            </p>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 text-blue-200 hover:text-white transition-colors touch-target"
          aria-label="Dismiss"
        >
          <FaTimes className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          onClick={handleUpdate}
          className="flex-1 bg-white text-blue-600 px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors touch-target min-h-[44px]"
        >
          Update Now
        </button>
        <button
          onClick={handleDismiss}
          className="px-3 py-2 border border-blue-400 rounded-md text-sm font-medium text-white hover:bg-blue-700 transition-colors touch-target min-h-[44px]"
        >
          Later
        </button>
      </div>
    </div>
  );
}

